import React, { useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { ClipboardPaste, Calendar, Tag, Clock, Check, X, Loader2, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-Client-Id': axios.defaults.headers.common['X-Client-Id'] || '',
  };
}

const CATEGORY_COLORS = {
  'food trucks': 'bg-orange-100 text-orange-700 border-orange-200',
  'music bingo': 'bg-purple-100 text-purple-700 border-purple-200',
  'team trivia': 'bg-teal-100 text-teal-700 border-teal-200',
  'save the date': 'bg-amber-100 text-amber-700 border-amber-200',
  'live music': 'bg-pink-100 text-pink-700 border-pink-200',
  'music': 'bg-pink-100 text-pink-700 border-pink-200',
};

function getCategoryColor(category) {
  if (!category) return 'bg-gray-100 text-gray-600 border-gray-200';
  const lower = category.toLowerCase();
  for (const [key, val] of Object.entries(CATEGORY_COLORS)) {
    if (lower.includes(key)) return val;
  }
  return 'bg-blue-100 text-blue-700 border-blue-200';
}

export function PasteImport({ isOpen, onClose, onImportComplete }) {
  const [step, setStep] = useState('paste'); // 'paste' | 'preview' | 'importing'
  const [text, setText] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const { toast } = useToast();

  const handleParse = async () => {
    if (!text.trim()) {
      toast({ title: 'Nothing to parse', description: 'Paste some text first', variant: 'destructive' });
      return;
    }
    setIsParsing(true);
    try {
      const res = await fetch(`${API_URL}/api/import/paste/preview`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ text, year }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Parse failed');
      if (data.events.length === 0) {
        toast({ title: 'No events found', description: 'Could not detect any dates/events in the text. Try a different format.', variant: 'destructive' });
        return;
      }
      setEvents(data.events);
      setStep('preview');
    } catch (err) {
      toast({ title: 'Parse error', description: err.message, variant: 'destructive' });
    } finally {
      setIsParsing(false);
    }
  };

  const handleRemoveEvent = (idx) => {
    setEvents(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUpdateEvent = (idx, field, value) => {
    setEvents(prev => prev.map((ev, i) => i === idx ? { ...ev, [field]: value } : ev));
  };

  const handleConfirmImport = async () => {
    if (events.length === 0) return;
    setStep('importing');
    try {
      const res = await fetch(`${API_URL}/api/import/paste/confirm`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ events, year }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      toast({ title: `Imported ${data.count} events`, description: 'All events added as draft posts' });
      if (onImportComplete) onImportComplete(data);
      handleClose();
    } catch (err) {
      toast({ title: 'Import error', description: err.message, variant: 'destructive' });
      setStep('preview');
    }
  };

  const handleClose = () => {
    setText('');
    setEvents([]);
    setStep('paste');
    setExpandedIdx(null);
    onClose();
  };

  // Group events by category
  const grouped = {};
  events.forEach((ev, idx) => {
    const cat = ev.category || 'Other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push({ ...ev, _idx: idx });
  });

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="paste-import-modal">
        <DialogTitle className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--brand-primary)' }}>
          <ClipboardPaste className="w-5 h-5" style={{ color: 'var(--brand-secondary)' }} />
          {step === 'paste' ? 'Paste & Import' : step === 'preview' ? 'Review Events' : 'Importing...'}
        </DialogTitle>

        {step === 'paste' && (
          <div className="space-y-4 flex-1 overflow-y-auto">
            <p className="text-sm text-gray-500">
              Paste an email, event list, or schedule below. The parser will detect dates, event names, times, and categories automatically.
            </p>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={"Paste event text here...\n\nExample:\nAPRIL\n\nLive Music Saturdays 2-5 pm:\n  April 4: Jon Spear\n  April 11: Ty Burkhardt\n\nFood Trucks:\n  Saturday April 18: BBQ Truck, 1-5 pm"}
              rows={14}
              className="font-mono text-sm border-gray-200 focus:border-[#3980C3] resize-none"
              data-testid="paste-text-input"
            />
            <div className="flex items-center gap-3">
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Year:</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value) || new Date().getFullYear())}
                className="w-20 px-2 py-1 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#3980C3]"
                data-testid="paste-year-input"
              />
            </div>
          </div>
        )}

        {step === 'preview' && (
          <div className="flex-1 overflow-y-auto space-y-4 pr-1 -mr-1 max-h-[60vh]">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Found <span className="font-bold" style={{ color: 'var(--brand-primary)' }}>{events.length}</span> events. Review and edit before importing.
              </p>
              <Button variant="ghost" size="sm" onClick={() => setStep('paste')} className="text-xs text-gray-500" data-testid="back-to-paste-btn">
                Back to edit
              </Button>
            </div>

            {Object.entries(grouped).map(([category, items]) => (
              <div key={category} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${getCategoryColor(category)}`}>
                    {category}
                  </span>
                  <span className="text-xs text-gray-400">{items.length} event{items.length !== 1 ? 's' : ''}</span>
                </div>

                {items.map((ev) => (
                  <div
                    key={ev._idx}
                    className="bg-white rounded-lg border border-gray-200 overflow-hidden transition-shadow hover:shadow-sm"
                    data-testid={`parsed-event-${ev._idx}`}
                  >
                    <div
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                      onClick={() => setExpandedIdx(expandedIdx === ev._idx ? null : ev._idx)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--brand-accent)' }} />
                          <span className="text-xs text-gray-500">
                            {format(new Date(ev.scheduled_date), 'EEE, MMM d')}
                          </span>
                          {ev.time && (
                            <>
                              <Clock className="w-3 h-3 text-gray-400 flex-shrink-0" />
                              <span className="text-xs text-gray-500">{ev.time}</span>
                            </>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 truncate mt-0.5">{ev.title}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveEvent(ev._idx); }}
                          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove event"
                          data-testid={`remove-event-${ev._idx}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        {expandedIdx === ev._idx ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                      </div>
                    </div>

                    {expandedIdx === ev._idx && (
                      <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-2">
                        <div>
                          <label className="text-[10px] font-semibold text-gray-400 uppercase">Title</label>
                          <input
                            value={ev.title}
                            onChange={(e) => handleUpdateEvent(ev._idx, 'title', e.target.value)}
                            className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-[#3980C3]"
                            data-testid={`edit-title-${ev._idx}`}
                          />
                        </div>
                        <div>
                          <label className="text-[10px] font-semibold text-gray-400 uppercase">Content</label>
                          <textarea
                            value={ev.content}
                            onChange={(e) => handleUpdateEvent(ev._idx, 'content', e.target.value)}
                            rows={2}
                            className="w-full text-sm px-2 py-1 border border-gray-200 rounded focus:outline-none focus:border-[#3980C3] resize-none"
                            data-testid={`edit-content-${ev._idx}`}
                          />
                        </div>
                        {ev.theme && (
                          <div className="flex items-center gap-1">
                            <Tag className="w-3 h-3 text-gray-400" />
                            <span className="text-xs text-gray-500">Theme: {ev.theme}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {step === 'importing' && (
          <div className="flex-1 flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3" style={{ color: 'var(--brand-accent)' }} />
              <p className="text-sm text-gray-500">Creating {events.length} posts...</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="border-gray-200" data-testid="paste-cancel-btn">
            Cancel
          </Button>
          {step === 'paste' && (
            <Button onClick={handleParse} disabled={isParsing || !text.trim()} className="text-white" style={{ backgroundColor: 'var(--brand-primary)' }} data-testid="parse-text-btn">
              {isParsing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ClipboardPaste className="w-4 h-4 mr-2" />}
              Parse Events
            </Button>
          )}
          {step === 'preview' && (
            <Button onClick={handleConfirmImport} disabled={events.length === 0} className="text-white" style={{ backgroundColor: 'var(--brand-secondary)' }} data-testid="confirm-import-btn">
              <Check className="w-4 h-4 mr-2" />
              Import {events.length} Events
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
